/* global theme */

/**
 * Complete the Look on the product page (snippets/complete-the-look.liquid):
 * - Each piece's images scroll natively (swipe on touch), with dots showing
 *   the current one. With a mouse, hovering shows the second image and the
 *   arrows step through them; leaving goes back to the first.
 * - Clicking a size selects it for that piece and shows it on the Add button.
 * - Add adds the piece in the selected size (or the only variant) to the
 *   cart, without leaving the page, then updates the header and cart drawer
 *   and follows the theme's "after add to cart" setting, as the main Add to
 *   cart does.
 * Added lines carry a hidden _complete_the_look line item property (the
 * product being viewed), kept separate from the cart upsell's _upsell.
 */
class CompleteTheLook extends HTMLElement {
  connectedCallback() {
    this.boundHandleClick = this.handleClick.bind(this);
    this.addEventListener('click', this.boundHandleClick);

    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.querySelectorAll('[data-look-media]').forEach((media) => this.initMedia(media));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundHandleClick);
  }

  /**
   * Set up a piece's images: dots follow scrolling, and with a mouse,
   * hovering shows the second image.
   * @param {Element} media - The piece's images container.
   */
  initMedia(media) {
    const slides = media.querySelector('[data-look-slides]');
    if (!slides || slides.children.length < 2) return;

    let frame;
    slides.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => CompleteTheLook.updateMedia(media));
    }, { passive: true });

    media.addEventListener('mouseenter', () => {
      if (this.hoverQuery.matches && CompleteTheLook.getSlideIndex(slides) === 0) {
        CompleteTheLook.goToSlide(slides, 1, 'auto');
      }
    });
    media.addEventListener('mouseleave', () => {
      if (this.hoverQuery.matches) CompleteTheLook.goToSlide(slides, 0, 'auto');
    });

    CompleteTheLook.updateMedia(media);
  }

  /**
   * @param {Element} slides - Scrolling images container.
   * @returns {number} Index of the image in view.
   */
  static getSlideIndex(slides) {
    return Math.round(Math.abs(slides.scrollLeft) / slides.clientWidth) || 0;
  }

  /**
   * Scroll an image into view.
   * @param {Element} slides - Scrolling images container.
   * @param {number} index - Image index (clamped to the images there are).
   * @param {string} [behavior='smooth'] - Scroll behaviour.
   */
  static goToSlide(slides, index, behavior = 'smooth') {
    const clamped = Math.max(0, Math.min(index, slides.children.length - 1));
    const direction = getComputedStyle(slides).direction === 'rtl' ? -1 : 1;
    slides.scrollTo({ left: clamped * slides.clientWidth * direction, behavior });
  }

  /**
   * Mark the current dot, and whether the first or last image is in view (to
   * hide the arrow that can't go further).
   * @param {Element} media - The piece's images container.
   */
  static updateMedia(media) {
    const slides = media.querySelector('[data-look-slides]');
    const index = CompleteTheLook.getSlideIndex(slides);
    media.querySelectorAll('.complete-the-look__dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
    media.classList.toggle('is-first', index === 0);
    media.classList.toggle('is-last', index === slides.children.length - 1);
  }

  /**
   * @param {object} evt - Click event.
   */
  handleClick(evt) {
    const arrow = evt.target.closest('[data-look-prev], [data-look-next]');
    if (arrow) {
      const slides = arrow.closest('[data-look-media]').querySelector('[data-look-slides]');
      const step = arrow.hasAttribute('data-look-next') ? 1 : -1;
      CompleteTheLook.goToSlide(slides, CompleteTheLook.getSlideIndex(slides) + step);
      return;
    }

    const sizeBtn = evt.target.closest('[data-size-variant-id]');
    if (sizeBtn) {
      this.selectSize(sizeBtn);
      return;
    }

    const addBtn = evt.target.closest('[data-add]');
    if (addBtn) this.handleAdd(addBtn);
  }

  /**
   * Select a size for a piece.
   * @param {Element} sizeBtn - The size's button.
   */
  selectSize(sizeBtn) {
    const item = sizeBtn.closest('[data-look-item]');
    item.querySelectorAll('[data-size-variant-id]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn === sizeBtn ? 'true' : 'false');
    });
    const addBtn = item.querySelector('[data-add]');
    const size = sizeBtn.textContent.trim();
    addBtn.dataset.variantId = sizeBtn.dataset.sizeVariantId;
    const sizeLabel = addBtn.querySelector('[data-add-size]');
    if (sizeLabel) sizeLabel.textContent = size;
    if (addBtn.dataset.labelTemplate) {
      addBtn.setAttribute('aria-label', addBtn.dataset.labelTemplate.replace('__SIZE__', size));
    }
    this.setError(item, false);
  }

  /**
   * Add a piece, or ask for a size if one is needed and not chosen.
   * @param {Element} addBtn - The piece's Add button.
   */
  handleAdd(addBtn) {
    if (this.adding) return;
    const item = addBtn.closest('[data-look-item]');
    if (!addBtn.dataset.variantId) {
      this.setError(item, this.dataset.chooseSizeText);
      const firstSize = item.querySelector('[data-size-variant-id]');
      if (firstSize) firstSize.focus();
      return;
    }
    this.addToCart(item, addBtn);
  }

  /**
   * Add a variant to the cart.
   * @param {Element} item - The piece's container.
   * @param {Element} addBtn - The piece's Add button, with a data-variant-id.
   */
  async addToCart(item, addBtn) {
    const variantId = Number(addBtn.dataset.variantId);
    const sectionsToUpdate = ['page-header', 'cart-drawer']
      .map((sel) => document.querySelector(sel))
      .filter((el) => el && el.dataset.sectionId);

    this.adding = true;
    this.setError(item, false);
    this.querySelectorAll('[data-add]').forEach((el) => el.setAttribute('aria-disabled', 'true'));
    addBtn.classList.add('is-loading');

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { _complete_the_look: this.dataset.look } }],
          sections: sectionsToUpdate.map((el) => el.dataset.sectionId).join(','),
          sections_url: window.location.pathname
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || this.dataset.errorText);
      }

      if (theme.settings.afterAddToCart === 'page') {
        window.location.href = theme.routes.cart;
      } else {
        if (data.sections && sectionsToUpdate.every((el) => data.sections[el.dataset.sectionId])) {
          sectionsToUpdate.forEach((el) => el.updateFromCartChange(data.sections[el.dataset.sectionId]));
        } else {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
        }

        if (theme.settings.afterAddToCart === 'notification') {
          const template = document.getElementById('AddedNotification');
          const addedItem = data.items && data.items[0];
          if (template && addedItem) {
            const notification = template.content.firstElementChild.cloneNode(true);
            notification.dataset.productTitle = addedItem.product_title;
            const container = document.querySelector('.pageheader--sticky') || document.body;
            container.appendChild(notification);
          }
        } else if (theme.settings.afterAddToCart === 'drawer') {
          const drawer = document.querySelector('.js-cart-drawer');
          if (drawer) drawer.open(addBtn);
        }
      }

      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId, completeTheLook: this.dataset.look }
      }));
    } catch (error) {
      this.setError(item, error.message || this.dataset.errorText);
      // The cart may have changed despite the error.
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
    }

    this.querySelectorAll('[aria-disabled="true"]').forEach((el) => el.removeAttribute('aria-disabled'));
    addBtn.classList.remove('is-loading');
    this.adding = false;
  }

  /**
   * Show or hide a piece's error message.
   * @param {Element} item - The piece's container.
   * @param {string|boolean} message - Message to show, or false to hide.
   */
  setError(item, message) {
    const errorEl = item && item.querySelector('.complete-the-look__error');
    if (!errorEl) return;
    errorEl.textContent = message || '';
    errorEl.hidden = !message;
  }
}

if (!customElements.get('complete-the-look')) {
  customElements.define('complete-the-look', CompleteTheLook);
}
